import React from 'react';
import { Box, Typography } from '@mui/material';
import { CreditCard } from '@mui/icons-material';
import { FormSection } from '../../form/FormSection';
import { ResponsiveInput } from '../../form/ResponsiveInput';
import { FORM_SECTIONS } from '../../../config/formSections';
import { FORM_STYLES } from '../../../styles/formStyles';
import { FormValues } from '../CreateStep1V3';

interface EconomicSectionProps {
  values: FormValues;
  handleChange: (name: string) => (value: any) => void;
  errors?: Record<string, string | undefined>;
  onBlur?: (fieldName: string) => () => void;
  shouldShowError?: (fieldName: string) => boolean;
}

export const EconomicSection: React.FC<EconomicSectionProps> = ({
  values,
  handleChange,
  errors = {},
  onBlur,
  shouldShowError = () => false
}) => {
  const sectionConfig = FORM_SECTIONS.economic;

  return (
    <FormSection
      icon={sectionConfig.icon}
      iconColor={sectionConfig.color}
      title={sectionConfig.titleKey}
    >
      {/* Avgifts-notice */}
      <Box sx={{
        mb: 4,
        p: 2.5,
        backgroundColor: '#EFF6FF',
        borderLeft: '4px solid #62B8E9',
        borderRadius: '8px'
      }}>
        <Typography sx={{
          fontSize: '14px',
          lineHeight: '1.6',
          color: '#1E40AF',
          fontWeight: 500
        }}>
          Notera att Hyria debiterar en avgift på 3.95% av månadshyran. Detta bör beaktas vid hyressättningen.
        </Typography>
      </Box>

      {/* Hyra och deposition */}
      <Box sx={{ ...FORM_STYLES.layout.twoColumn, mb: 4 }}>
        <ResponsiveInput
          label="Hyresavgift"
          value={values.rentalFee}
          onChange={handleChange('rentalFee')}
          onBlur={onBlur?.('rentalFee')}
          type="number"
          placeholder="12000"
          unit="kr/månad"
          infoText="Hyresavgiften avser vilken hyra ni önskar ta betalt från er hyresgäst."
          required={true}
          error={shouldShowError('rentalFee') && !!errors.rentalFee}
          helperText={shouldShowError('rentalFee') ? errors.rentalFee : ''}
          autoComplete="off"
        />

        <ResponsiveInput
          label="Deposition"
          value={values.deposit}
          onChange={handleChange('deposit')}
          onBlur={onBlur?.('deposit')}
          type="number"
          placeholder="24000"
          unit="kr"
          infoText="Depositionsbelopp som ska betalas av hyresgästen innan inflyttning."
          required={true}
          error={shouldShowError('deposit') && !!errors.deposit}
          helperText={shouldShowError('deposit') ? errors.deposit : ''}
          autoComplete="off"
        />
      </Box>

      {/* Bankuppgifter */}
      <Box sx={{ mt: 4, pt: 4, borderTop: '2px solid #E2E8F0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Box sx={{
            width: '48px',
            height: '48px',
            backgroundColor: '#62B8E9',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 3
          }}>
            <CreditCard sx={{ fontSize: '24px', color: 'white' }} />
          </Box>
          <Typography sx={{
            fontSize: '24px',
            fontWeight: 700,
            color: '#2D3748',
            fontFamily: 'system-ui, -apple-system, sans-serif'
          }}>
            Bankuppgifter
          </Typography>
        </Box>

        <Box sx={FORM_STYLES.layout.twoColumn}>
          <ResponsiveInput
            label="Clearingnummer"
            value={values.clearingnummer}
            onChange={handleChange('clearingnummer')}
            onBlur={onBlur?.('clearingnummer')}
            type="text"
            placeholder="8000-0"
            infoText="Bankuppgifter samlas för att veta vilket bankkonto hyresgästen ska betala in hyran på."
            required={true}
            error={shouldShowError('clearingnummer') && !!errors.clearingnummer}
            helperText={shouldShowError('clearingnummer') ? errors.clearingnummer : ''}
            autoComplete="off"
          />

          <ResponsiveInput
            label="Kontonummer"
            value={values.bankAccountNumber}
            onChange={handleChange('bankAccountNumber')}
            onBlur={onBlur?.('bankAccountNumber')}
            type="text"
            placeholder="123 456 789"
            infoText="Bankuppgifter samlas för att veta vilket bankkonto hyresgästen ska betala in hyran på."
            required={true}
            error={shouldShowError('bankAccountNumber') && !!errors.bankAccountNumber}
            helperText={shouldShowError('bankAccountNumber') ? errors.bankAccountNumber : ''}
            autoComplete="off"
          />
        </Box>
      </Box>
    </FormSection>
  );
}; 